import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Download, Loader2, Mail } from 'lucide-react';
import PageHero from '../components/PageHero';
import { Reveal, FadeIn } from '../components/Reveal';
import { IMAGES } from '../lib/site';
import { api } from '../lib/api';

const PressKitButton = () => {
    const [downloading, setDownloading] = useState(false);
    const download = async () => {
        setDownloading(true);
        try {
            const r = await api.get('/press-kit', { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'CommPro-Worldwide-Press-Kit.pdf';
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Press kit downloaded.');
        } catch {
            toast.error('Press kit unavailable right now. Please try again.');
        } finally {
            setDownloading(false);
        }
    };
    return (
        <button
            data-testid="press-kit-download-btn"
            onClick={download}
            disabled={downloading}
            className="group inline-flex items-center gap-3 bg-bronze text-ink font-mono text-[11px] uppercase tracking-[0.22em] px-10 py-4 rounded-full relative overflow-hidden disabled:opacity-60 whitespace-nowrap"
        >
            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
            {downloading ? <Loader2 size={14} className="relative animate-spin" /> : <Download size={14} className="relative" />}
            <span className="relative">{downloading ? 'Preparing…' : 'Download Press Kit'}</span>
        </button>
    );
};

const FEATURES = [
    {
        outlet: 'Global Business Review',
        title: 'The quiet architects behind cross-border market entries',
        tag: 'Firm Profile',
        image: IMAGES.conference,
    },
    {
        outlet: 'Institutional Wire',
        title: 'Public-private coalitions are the new market-access strategy',
        tag: 'Thought Leadership',
        image: IMAGES.skyline,
    },
    {
        outlet: 'The Policy Ledger',
        title: 'How C-suites are programming influence like infrastructure',
        tag: 'Op-Ed Placement',
        image: IMAGES.boardroom,
    },
];

const CAPABILITIES = [
    ['Editorial board access', 'Standing relationships with business, trade, and policy desks across major markets.'],
    ['Broadcast placement', 'Segment strategy and booking across financial and global affairs networks.'],
    ['Narrative engineering', 'Message architecture pressure-tested against journalists, analysts, and regulators.'],
    ['Rapid response', 'Issues management and comment strategy when the story moves fast.'],
];

export default function Media() {
    return (
        <>
            <PageHero
                overline="Media Relations"
                lines={['The story is', 'the strategy.']}
                sub="Media relations sits at the core of the CommPro model. We place principals and narratives where markets are shaped — and convert coverage into adoption, partnerships, and pipeline."
            />

            <section className="py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                            <span className="inline-block w-10 h-px bg-bronze" />
                            Recent Visibility
                        </p>
                        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-cream">
                            Executive visibility <span className="text-bronze">highlights.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
                        {FEATURES.map((f, i) => (
                            <Reveal key={f.title} delay={i * 0.08}>
                                <article data-testid={`press-feature-${i}`} className="group bg-ink h-full flex flex-col">
                                    <div className="overflow-hidden">
                                        <img
                                            src={f.image}
                                            alt={`${f.title} — ${f.outlet} feature on CommPro Worldwide media relations`}
                                            className="w-full aspect-[16/10] object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                                        />
                                    </div>
                                    <div className="p-7 flex flex-col flex-1">
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bronze">
                                                {f.outlet}
                                            </span>
                                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-fog">
                                                {f.tag}
                                            </span>
                                        </div>
                                        <h3 className="mt-4 font-serif text-xl text-cream leading-snug group-hover:italic transition-all duration-300">
                                            {f.title}
                                        </h3>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="dark-section py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
                    <div>
                        <Reveal>
                            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-bronze" />
                                Capabilities
                            </p>
                            <h2 className="mt-5 font-serif text-3xl sm:text-4xl font-medium tracking-tight text-cream">
                                Media relations, <span className="text-bronze">practiced as counsel.</span>
                            </h2>
                        </Reveal>
                        <div className="mt-10 border-t border-line">
                            {CAPABILITIES.map(([t, d], i) => (
                                <Reveal key={t} delay={i * 0.06}>
                                    <div className="py-6 border-b border-line grid grid-cols-1 sm:grid-cols-5 gap-2">
                                        <h3 className="sm:col-span-2 font-serif text-lg text-cream">{t}</h3>
                                        <p className="sm:col-span-3 text-sm text-fog leading-relaxed">{d}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <FadeIn delay={0.15}>
                        <div className="relative h-full min-h-[24rem]">
                            <div className="absolute -inset-3 border border-bronze/25 translate-x-5 translate-y-5 pointer-events-none" />
                            <img
                                src={IMAGES.podium}
                                alt="Press conference podium with microphones"
                                className="relative w-full h-full object-cover grayscale-[30%] contrast-110"
                            />
                        </div>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 sm:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                    <Reveal>
                        <h2 className="font-serif text-2xl sm:text-3xl text-cream max-w-xl">
                            Press & media inquiries
                        </h2>
                        <p className="mt-3 text-sm sm:text-base text-fog max-w-xl leading-relaxed">
                            For interview requests, comment, or the CommPro press kit, contact our media desk
                            at <span className="text-bronze">press@commpro.com</span> or submit a
                            formal inquiry.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="flex flex-col sm:flex-row gap-4">
                        <PressKitButton />
                        <Link
                            to="/contact?interest=Media%20Relations"
                            data-testid="media-inquiry-btn"
                            className="group inline-flex items-center gap-3 border border-bronze text-bronze font-mono text-[11px] uppercase tracking-[0.22em] px-10 py-4 rounded-full hover:bg-bronze hover:text-ink transition-colors duration-500 whitespace-nowrap"
                        >
                            <Mail size={14} />
                            Contact the Media Desk
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
